import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, KeyRound, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_ROLES = [
  'Super Admin',
  'Project Head',
  'Key Account Manager',
  'Site Supervisor',
  'Billing Manager',
  'Billing Executive',
  'Account Manager',
  'Delivery Manager'
];

// Roles that each user type can assign
const ALLOWED_ROLES_BY_USER = {
  'Super Admin': ALL_ROLES, // Can assign all roles
  'Project Head': ['Site Supervisor', 'Billing Manager', 'Billing Executive', 'Account Manager', 'Delivery Manager'], // Cannot assign Super Admin, Project Head, Key Account Manager
  'Key Account Manager': ['Project Head', 'Site Supervisor', 'Billing Manager', 'Billing Executive', 'Account Manager', 'Delivery Manager'] // Cannot assign Super Admin, Key Account Manager
};

export default function UsersPage() {
  const { user, hasAnyRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Onboard new user state
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);

  // Determine which roles the current user can assign
  const getCurrentUserRole = () => {
    if (hasAnyRole(['Super Admin'])) return 'Super Admin';
    if (hasAnyRole(['Key Account Manager'])) return 'Key Account Manager';
    if (hasAnyRole(['Project Head'])) return 'Project Head';
    return null;
  };
  
  const allowedRoles = ALLOWED_ROLES_BY_USER[getCurrentUserRole()] || [];
  const isSuperAdmin = hasAnyRole(['Super Admin']);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetDialogOpen(true);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${selectedUser.name}`);
      setResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleToggleAvailability = async (targetUser) => {
    // Don't allow toggling own account
    if (targetUser.id === user?.id) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    
    setTogglingUserId(targetUser.id);
    try {
      const response = await axios.put(`${API}/users/${targetUser.id}/toggle-availability`);
      toast.success(response.data.message);
      fetchUsers(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user availability');
    } finally {
      setTogglingUserId(null);
    }
  };

  const openOnboardDialog = () => {
    setNewUserName('');
    setNewUserEmail('');
    setNewUserPassword('');
    setSelectedRoles([]);
    setOnboardDialogOpen(true);
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role) 
        : [...prev, role]
    );
  };

  const handleCreateUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (selectedRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/auth/register`, {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        roles: selectedRoles
      });
      toast.success(`User ${newUserName} created successfully`);
      setOnboardDialogOpen(false);
      fetchUsers(); // Reload the user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading users...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                User Management
              </h1>
              <p className="text-muted-foreground mt-2">
                {users.length} users registered
              </p>
            </div>
          </div>
          <Button
            onClick={openOnboardDialog}
            className="bg-[#DC143C] hover:bg-[#b91130]"
            data-testid="onboard-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Onboard new User
          </Button>
        </div>

        <Card className="overflow-hidden glass-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#1B273D] text-white">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                  {isSuperAdmin && (
                    <th className="px-4 py-3 text-center text-sm font-semibold">Availability</th>
                  )}
                  <th className="px-4 py-3 text-left text-sm font-semibold">Roles</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Joined</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-medium">{u.name}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {u.email}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            checked={u.is_active !== false}
                            onCheckedChange={() => handleToggleAvailability(u)}
                            disabled={togglingUserId === u.id || u.id === user?.id}
                            data-testid={`toggle-availability-${u.id}`}
                          />
                          <span className={`text-xs font-medium ${u.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                            {u.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((role, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isSuperAdmin ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResetDialog(u)}
                          className="text-xs"
                          data-testid={`reset-password-btn-${u.id}`}
                        >
                          <KeyRound className="h-3 w-3 mr-1" />
                          Password Reset
                        </Button>
                      ) : (
                        <Badge variant="secondary" className="text-xs">View Only</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                User: <span className="font-medium">{selectedUser?.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Email: <span className="font-medium">{selectedUser?.email}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">Set new password *</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Re-enter password *</Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                data-testid="confirm-password-input"
              />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
              {newPassword && confirmPassword && newPassword === confirmPassword && (
                <p className="text-xs text-green-500">Passwords match</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handlePasswordReset}
                disabled={resetting || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="flex-1 bg-[#DC143C] hover:bg-[#b91130]"
                data-testid="confirm-reset-btn"
              >
                {resetting ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboard New User Dialog */}
      <Dialog open={onboardDialogOpen} onOpenChange={setOnboardDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex flex-col items-center gap-4">
              <div className="w-14 h-14 bg-[#DC143C] rounded-xl flex items-center justify-center">
                <UserPlus className="h-7 w-7 text-white" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold italic text-[#DC143C]">Create Account</h2>
                <p className="text-sm font-normal text-gray-500 mt-1">Register for Radiants Civiletric Portal</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                placeholder="John Doe"
                data-testid="new-user-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="user_email">Email</Label>
              <Input
                id="user_email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="new-user-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="user_password">Password</Label>
              <Input
                id="user_password"
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="********"
                data-testid="new-user-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Roles (one or multiple)</Label>
              {allowedRoles.length === 0 ? (
                <p className="text-sm text-red-500">You don't have permission to create users.</p>
              ) : (
              <div className="rounded-xl p-4 bg-gradient-to-br from-white/60 to-white/30 backdrop-blur-md border border-white/40 shadow-lg">
                <div className="grid grid-cols-2 gap-3">
                  {allowedRoles.map((role) => (
                    <div 
                      key={role} 
                      className="flex items-center space-x-2 p-2 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30 hover:bg-white/70 transition-all duration-200 shadow-sm"
                    >
                      <Checkbox
                        id={`role-${role}`}
                        checked={selectedRoles.includes(role)}
                        onCheckedChange={() => toggleRole(role)}
                        className="border-[#DC143C] data-[state=checked]:bg-[#DC143C]"
                      />
                      <label
                        htmlFor={`role-${role}`}
                        className="text-sm cursor-pointer font-medium text-gray-700"
                      >
                        {role}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
            
            <Button
              onClick={handleCreateUser}
              disabled={creating || !newUserName || !newUserEmail || !newUserPassword || selectedRoles.length === 0}
              className="w-full bg-[#DC143C] hover:bg-[#b91130]"
              data-testid="create-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {creating ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
